<template>
  <router-view />

  <button
    v-if="showGlobalChatButton"
    type="button"
    class="rrpl-global-chat-btn"
    :title="globalChatTitle"
    @click="goToChatCenter"
  >
    <i class="ri-chat-3-line" />
    <span
      v-if="unreadChatCount > 0"
      class="badge rounded-pill bg-danger rrpl-global-chat-badge"
    >
      {{ unreadChatCount > 99 ? "99+" : unreadChatCount }}
    </span>
  </button>
</template>

<script>
import Swal from "sweetalert2";
import store from "@/state/store";
import { ContaChatSocket } from "@/services/contaChat";

const UNREAD_KEY = {
  admin: "rrpl_chat_unread_admin",
  user: "rrpl_chat_unread_user",
};

export default {
  name: "App",
  data() {
    return {
      unreadChatCount: 0,
      chatSocket: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store?.state?.auth?.currentUser ?? null;
    },
    currentRole() {
      return String(this.currentUser?.role || "").toLowerCase();
    },
    isAuthenticated() {
      return Boolean(this.currentUser?.token && (this.currentRole === "admin" || this.currentRole === "user"));
    },
    showGlobalChatButton() {
      return this.isAuthenticated;
    },
    globalChatTitle() {
      return this.currentRole === "admin" ? "Mensagens de suporte" : "Suporte RRPL";
    },
  },
  watch: {
    currentUser: {
      immediate: true,
      handler() {
        this.setupGlobalChatNotifications();
      },
    },
    "$route.fullPath"() {
      this.handleRouteReadState();
    },
  },
  mounted() {
    store.dispatch("siteBranding/fetchBranding");
    this.setupGlobalChatNotifications();
    window.addEventListener("storage", this.onStorageChange);
  },
  beforeUnmount() {
    this.stopChatSocket();
    window.removeEventListener("storage", this.onStorageChange);
  },
  methods: {
    getUnreadStorageKey() {
      return UNREAD_KEY[this.currentRole] || null;
    },
    emitUnreadChanged() {
      window.dispatchEvent(
        new CustomEvent("rrpl-chat-unread-updated", {
          detail: { role: this.currentRole, count: this.unreadChatCount },
        }),
      );
    },
    loadUnreadFromStorage() {
      const key = this.getUnreadStorageKey();
      if (!key) {
        this.unreadChatCount = 0;
        return;
      }
      const n = Number(localStorage.getItem(key) || "0");
      this.unreadChatCount = Number.isFinite(n) ? n : 0;
      this.emitUnreadChanged();
    },
    saveUnreadToStorage() {
      const key = this.getUnreadStorageKey();
      if (!key) return;
      localStorage.setItem(key, String(this.unreadChatCount));
      this.emitUnreadChanged();
    },
    clearUnread() {
      this.unreadChatCount = 0;
      this.saveUnreadToStorage();
    },
    isChatScreenOpen() {
      if (this.currentRole === "admin") return this.$route?.path === "/chat";
      if (this.currentRole === "user") {
        return this.$route?.path === "/minha-conta" && this.$route?.query?.tab === "suporte";
      }
      return false;
    },
    handleRouteReadState() {
      if (this.isChatScreenOpen()) {
        this.clearUnread();
      }
    },
    stopChatSocket() {
      this.chatSocket?.disconnect();
      this.chatSocket = null;
    },
    setupGlobalChatNotifications() {
      this.stopChatSocket();
      this.loadUnreadFromStorage();

      if (!this.isAuthenticated) return;

      this.handleRouteReadState();
      this.chatSocket = new ContaChatSocket({
        onMessage: (data) => {
          if (data?.type !== "message" || !data?.message) return;
          const sender = data.message.remetente_tipo;
          const relevant =
            (this.currentRole === "admin" && sender === "user") ||
            (this.currentRole === "user" && sender === "admin");
          if (!relevant) return;

          if (this.isChatScreenOpen()) return;

          this.unreadChatCount += 1;
          this.saveUnreadToStorage();

          const text = data.message.texto || data.message.ficheiro_nome || "Nova mensagem";
          Swal.fire({
            toast: true,
            position: "top-end",
            icon: "info",
            title: this.currentRole === "admin" ? "Nova mensagem de fã" : "Nova mensagem da equipa RRPL",
            text,
            timer: 3500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        },
        onClose: () => {
          this.chatSocket = null;
        },
      });
      this.chatSocket.connect();
    },
    goToChatCenter() {
      if (this.currentRole === "admin") {
        this.$router.push("/chat");
      } else if (this.currentRole === "user") {
        this.$router.push({ path: "/minha-conta", query: { tab: "suporte" } });
      }
      this.clearUnread();
    },
    onStorageChange(event) {
      const key = this.getUnreadStorageKey();
      if (!key || event.key !== key) return;
      this.loadUnreadFromStorage();
    },
  },
};
</script>

<style scoped>
.rrpl-global-chat-btn {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  z-index: 1200;
  width: 52px;
  height: 52px;
  border: 0;
  border-radius: 999px;
  background: #e31c24;
  color: #fff;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}
.rrpl-global-chat-btn:hover {
  background: #c7171e;
}
.rrpl-global-chat-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 22px;
  height: 22px;
  line-height: 22px;
  font-size: 0.68rem;
  padding: 0 6px;
}
</style>

<style>
/* Garante modal sempre acima do backdrop em todo o app */
body .modal-backdrop,
body .modal-backdrop.show {
  z-index: 1050 !important;
}

body .modal,
body .modal.show {
  z-index: 2000 !important;
}

body .modal .modal-dialog,
body .modal .modal-content {
  position: relative;
  z-index: 2001 !important;
}
</style>
